/**
 * Honest period-over-period delta for KPI cards.
 *
 * Replaces the inline %-change duplicated across MetricSummaryCards and
 * AnalyticsMetricCard, which showed a fake "0%" when the prior period was 0 and
 * a misleading "+100%" off a base of 1. Special-cases the zero denominator and
 * suppresses percentages on low-N baselines (where a % is statistically noise).
 */

export type Delta = {
  /** Full, honest form for tooltips / aria (e.g. "+20.0% vs prev"). */
  label: string;
  /** Compact, single-line form for the KPI chip (e.g. "+20.0%"). The arrow icon
   *  beside it already carries direction, so "vs prev" / arrow glyphs are dropped
   *  here — this is what stops the chip wrapping to two lines on narrow cards. */
  value: string;
  direction: 'up' | 'down' | 'flat';
};

/**
 * U+2212, the typographic minus — the one every other signed figure on
 * `/insights` prints ("−14%" on Formats vs your average, "−58% · 34 posts").
 * This formatter alone printed an ASCII hyphen, so one page read "(-97.1%)" on
 * a KPI tile and "−14%" on the card below it. A hyphen-minus is also narrower
 * than a digit in tabular figures, which makes a signed column ragged.
 */
const MINUS = '\u2212';

/** Below this prior-period baseline, a percentage is noise — show the raw delta. */
const LOW_N_FLOOR = 5;

export function formatDelta(current: number, prev: number): Delta {
  if (prev === 0 && current === 0) return { label: 'No change', value: '—', direction: 'flat' };
  if (prev === 0 && current > 0) return { label: 'New this period', value: 'New', direction: 'up' };
  // 🔴 `>= LOW_N_FLOOR`, not `> 0`. This branch used to run BEFORE the low-N
  // check below and so escaped it entirely: 3 -> 0 printed "-100%", which is
  // the very percentage-off-a-tiny-base this function was written to suppress.
  // A base under the floor falls through and prints the raw change ("-3").
  if (prev >= LOW_N_FLOOR && current === 0)
    return { label: `${MINUS}100%`, value: `${MINUS}100%`, direction: 'down' };

  // Low-N: a base below the floor makes % meaningless (1→2 reads as "+100%").
  // Show the raw change instead.
  if (prev < LOW_N_FLOOR) {
    const diff = current - prev;
    const sign = diff > 0 ? '+' : diff < 0 ? MINUS : '';
    // 🔴 ROUNDED. This printed the raw difference, which is fine for the whole
    // numbers this branch was written for (followers, posts) and wrong the
    // moment a provider reports fractions: Google Ads attributes CONVERSIONS
    // fractionally, so a campaign going 0.202058 → 9.797942 rendered the chip
    // as "+9.595884" — nine characters of false precision in an 11px slot,
    // measured live on 2026-08-18. Two decimals is as much as any of these
    // metrics can honestly carry, and whole numbers still print whole.
    const magnitude = Math.abs(diff);
    const shown = Number.isInteger(magnitude)
      ? String(magnitude)
      : magnitude.toLocaleString(undefined, { maximumFractionDigits: 2 });
    // A change too small to survive rounding is NOT a change. Without this, a
    // CTR that moved by 0.0004 rendered as "-0" — a minus sign in front of
    // nothing, coloured as a regression. Measured live on 2026-08-18.
    if (shown === '0') return { label: 'No change', value: '—', direction: 'flat' };
    return {
      label: `${sign}${shown} vs prev`,
      // No icon beside the delta — the sign carries direction.
      value: `${sign}${shown}`,
      direction: diff > 0 ? 'up' : diff < 0 ? 'down' : 'flat',
    };
  }

  const pct = ((current - prev) / prev) * 100;
  // 🔴 A MOVEMENT THAT ROUNDS AWAY IS NOT A MOVEMENT. `direction: pct >= 0`
  // could never return 'flat', so an UNCHANGED metric — 24 orders against 24
  // orders — printed "(+0.0%)" in success green, asserting a gain that did not
  // happen. The low-N branch above already encodes exactly this rule for raw
  // differences ("a change too small to survive rounding is NOT a change");
  // the percentage branch simply never got it. `deltaTone` renders 'flat'
  // neutral in both polarities, which is the honest colour for no change.
  if (Math.abs(pct) < 0.05) return { label: 'No change', value: '—', direction: 'flat' };
  const sign = pct >= 0 ? '+' : MINUS;
  const compact = `${sign}${Math.abs(pct).toFixed(1)}%`;
  return {
    label: `${compact} vs prev`,
    value: compact,
    direction: pct >= 0 ? 'up' : 'down',
  };
}

/**
 * Whether a metric's own nature says more is better.
 *
 * `true`  more is better — revenue, ROAS, AOV, CVR, CTR
 * `false` LESS is better — CAC, cost per click, cost per result
 * `null`  neither — spend, impressions, clicks. These are decisions or volume,
 *         not outcomes: spending more is not a win and not a loss on its own,
 *         so colouring them asserts a judgement nobody made.
 */
export type MetricPolarity = boolean | null;

export type DeltaTone = 'positive' | 'negative' | 'neutral';

/**
 * 🔴 THE POLARITY RULE, IN ONE PLACE. Design-system hard rule 28.
 *
 * Delta colour follows the METRIC'S polarity, never the sign of the change.
 * `CAC ↓ −45%` is GREEN and `AOV ↓ −15%` is RED — both are decreases. Any
 * implementation that colours on `value > 0` paints a 45% cost reduction as a
 * failure, which is exactly backwards on the number a marketer most wants to
 * see fall.
 *
 * It lives here, beside `formatDelta`, rather than at each render site, because
 * a rule restated per call site is a rule that will eventually disagree with
 * itself. The caller supplies the metric's declared polarity; this decides tone.
 */
/**
 * The ONE tone → class map. Lives beside `deltaTone` for the same reason the
 * polarity rule does — a colour restated per render site is a colour that will
 * eventually disagree with itself. `CampaignPerformancePanel` and
 * `MetricSummaryCards` are the two KPI surfaces and must not differ about what
 * green means.
 */
export const DELTA_TONE_CLASS: Record<DeltaTone, string> = {
  positive: 'text-success',
  negative: 'text-destructive',
  neutral: 'text-muted-foreground',
};

export function deltaTone(
  direction: Delta['direction'],
  higherIsBetter: MetricPolarity,
): DeltaTone {
  if (direction === 'flat' || higherIsBetter === null) return 'neutral';
  const isGood = direction === 'up' ? higherIsBetter : !higherIsBetter;
  return isGood ? 'positive' : 'negative';
}
